import { createContext } from 'react';
import { ApiEndpointCallStatesStore, createApiStore } from '../api';
import { appConfig } from '../configurations';
import { ErrorStore, IdleStore } from '../data-stores';
import { AwardResourcesPageUiStore } from '../pages/page-award-resources/award-resources-page-ui-store';
import { AwardPageUiStore } from '../pages/page-award/award-page-ui-store';
import { CountryAwardPageUiStore } from '../pages/page-country-award/country-award-page-ui-store';
import { CreateOrEditEmailTemplatePageUiStore } from '../pages/page-create-or-edit-email-template/create-or-edit-email-template-page-ui-store';
import { EditAwardDetailPageUiStore } from '../pages/page-edit-award-detail/edit-award-detail-page-ui-store';
import { EditRegionalPageUiStore } from '../pages/page-edit-regional/edit-regional-page-ui-store';
import { EmailPageUiStore } from '../pages/page-email/email-page-ui-store';
import { JudgingDashboardPageUiStore } from '../pages/page-judging-dashboard/judging-dashboard-page-ui-store';
import { JudgingResourcesPageUiStore } from '../pages/page-judging-resource/judging-resources-page-ui-store';
import { ManageAwardListPageUiStore } from '../pages/page-manage-award-list/manage-award-list-page-ui-store';
import { NominationDashboardPageUiStore } from '../pages/page-nomination-dashboard/nomination-dashboard-page-ui-store';
import { NominationForStaffPageUiStore } from '../pages/page-nomination-for-staff/nomination-for-staff-page-ui-store';
import { NominationWizardPageUiStore } from '../pages/page-nomination-wizard/nomination-wizard-page-ui-store';
import { NotificationsPageUiStore } from '../pages/page-notifications/notification-page-ui-store';
import { OperationDashboardPageUiStore } from '../pages/page-operation-dashboard/operation-dashboard-page-ui-store';
import { ProgramDashboardPageUiStore } from '../pages/page-program-dashboard/program-dashboard-page-ui-store';
import { ProgramDatesPageUiStore } from '../pages/page-program-dates/program-dates-page-ui-store';
import { ProgramsPageUiStore } from '../pages/page-programs/programs-page-ui-store';
import { QuestionnaireWizardPageUiStore } from '../pages/page-questionnaire-wizard/questionnaire-wizard-page-ui-store';
import { ReviewAndSubmitNominationPageUiStore } from '../pages/page-review-and-submit-nomination/review-and-submit-nomination-page-ui-store';
import { ReviewQuestionnairePageUiStore } from '../pages/page-review-questionnaire/review-questionnaire-page-ui-store';
import { SelectAwardPageUiStore } from '../pages/page-select-award/select-award-page-ui-store';
import { LeadAssignmentUiStore } from '../pages/shared-components/lead-assignment/lead-assignment-ui-store';
import { UserResourcesPageUiStore } from '../pages/page-user-resources/user-resources-page-ui-store';
import { WinnerRequirementPageUiStore } from '../pages/page-winner-requirement/winner-requirement-page-ui-store';
import { WinnersAndFinalistsPageUiStore } from '../pages/page-winners-and-finalists/winners-and-finalists-page-ui-store';
import { AwardStore } from '../stores/award-store';
import { CountryStore } from '../stores/country-store';
import { NotificationStore } from '../stores/notification-store';
import { PartnerTypeStore } from '../stores/partner-type-store';
import { TargetGroupStore } from '../stores/target-group-store';
import { TimeZoneStore } from '../stores/time-zone-store';
import { UserStore } from '../stores/user-store';

export function createRootStores() {
  const idleStore = new IdleStore();
  const errorStore = new ErrorStore();
  const apiEndpointCallStatesStore = new ApiEndpointCallStatesStore();
  const apiStore = createApiStore(
    appConfig.api.baseUrl,
    errorStore,
    apiEndpointCallStatesStore,
  );

  return {
    apiEndpointCallStatesStore,
    errorStore,
    idleStore,
    apiStore,
    userStore: new UserStore(),
    awardStore: new AwardStore(),
    countryStore: new CountryStore(),
    timeZoneStore: new TimeZoneStore(),
    targetGroupStore: new TargetGroupStore(),
    notificationStore: new NotificationStore(),
    partnerTypeStore: new PartnerTypeStore(),
    notificationPageUiStore: new NotificationsPageUiStore(),
    selectAwardPageUiStore: new SelectAwardPageUiStore(),
    nominationForStaffPageUiStore: new NominationForStaffPageUiStore(),
    nominationDashboardPageUiStore: new NominationDashboardPageUiStore(),
    createOrEditEmailTemplatePageUiStore: new CreateOrEditEmailTemplatePageUiStore(),
    editAwardDetailPageUiStore: new EditAwardDetailPageUiStore(),
    manageAwardListPageUiStore: new ManageAwardListPageUiStore(),
    nominationWizardPageUiStore: new NominationWizardPageUiStore(),
    questionnaireWizardPageUiStore: new QuestionnaireWizardPageUiStore(),
    winnerRequirementPageUiStore: new WinnerRequirementPageUiStore(),
    winnersAndFinalistsPageUiStore: new WinnersAndFinalistsPageUiStore(),
    programDatesPageUiStore: new ProgramDatesPageUiStore(),
    programsPageUiStore: new ProgramsPageUiStore(),
    operationDashboardPageUiStore: new OperationDashboardPageUiStore(),
    countryAwardPageUiStore: new CountryAwardPageUiStore(),
    userResourcesPageUiStore: new UserResourcesPageUiStore(),
    awardResourcesPageUiStore: new AwardResourcesPageUiStore(),
    judgingResourcesPageUiStore: new JudgingResourcesPageUiStore(),
    editRegionalPageUiStore: new EditRegionalPageUiStore(),
    emailPageUiStore: new EmailPageUiStore(),
    reviewQuestionnairePageUiStore: new ReviewQuestionnairePageUiStore(),
    programDashboardPageUiStore: new ProgramDashboardPageUiStore(),
    awardPageUiStore: new AwardPageUiStore(),
    judgingDashboardPageUiStore: new JudgingDashboardPageUiStore(),
    reviewAndSubmitNominationPageUiStore: new ReviewAndSubmitNominationPageUiStore(),
    leadAssignmentUiStore: new LeadAssignmentUiStore(),
  };
}

export type RootStores = ReturnType<typeof createRootStores>;

export const RootStoresContext = createContext<RootStores | undefined>(undefined);
